import { useCallback, useEffect, useRef, useState } from 'react';

/** Nothing queued, talking, or holding part-way through. */
export type ReadAloudState = 'idle' | 'speaking' | 'paused';

/**
 * Speaks a block of text through the browser's own speech synthesiser.
 *
 * This is not the "Read aloud" built into Edge — that is browser chrome and no page can reach it.
 * It is `window.speechSynthesis`, the web standard underneath the same idea, which every current
 * browser implements and which borrows the voices already installed on the machine. The point of
 * building it into the page is that the people it is for are the ones who will never find
 * Ctrl+Shift+U: someone who reads with difficulty is not helped by a feature they have to read about
 * first. A blind candidate needs none of this and arrives with their own screen reader.
 *
 * Three things about the API decide most of this file:
 *
 *  - `getVoices()` is empty on the first call in most browsers and fills in later, announced by
 *    `voiceschanged`. Read once at mount and the page speaks in whatever voice the OS defaults to.
 *  - That default is the trap worth naming: on a Windows machine set to another language, English
 *    text comes out in a voice with the wrong phonetics and is simply not understandable. Both the
 *    utterance's `lang` and an actual matching voice have to be set.
 *  - Chrome stops speaking part-way through any utterance longer than about fifteen seconds. The
 *    long-standing fix is to hand it short pieces, which is what `intoChunks` is for: an agreement
 *    read as one utterance would stop in the middle of a clause.
 */
export function useReadAloud(lang: string) {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  const [state, setState] = useState<ReadAloudState>('idle');
  const voiceRef = useRef<SpeechSynthesisVoice | null>(null);

  /**
   * Which run of `speak` owns the queue.
   *
   * `cancel()` does not settle its utterances before it returns — their handlers arrive afterwards,
   * still holding the closure of the run that queued them. Without a token to check, a `stop`
   * followed by a new `speak` lets the old run's `onend` resume a queue that was supposed to be
   * gone, and two voices talk over each other. Bumping it invalidates everything already in flight.
   */
  const runRef = useRef(0);

  useEffect(() => {
    if (!supported) return;

    const synth = window.speechSynthesis;

    function choose() {
      const voices = synth.getVoices();
      if (voices.length === 0) return;

      // Exact tag first, then the language without its region — "en-GB" is a far better answer for
      // "en-US" than whatever the machine happens to boot with. Some platforms report "en_US".
      const wanted = lang.toLowerCase();
      const base = wanted.split('-')[0];
      const tag = (voice: SpeechSynthesisVoice) => voice.lang.replace('_', '-').toLowerCase();

      voiceRef.current =
        voices.find((voice) => tag(voice) === wanted) ??
        voices.find((voice) => tag(voice).startsWith(`${base}-`)) ??
        voices.find((voice) => tag(voice) === base) ??
        null;
    }

    choose();
    synth.addEventListener('voiceschanged', choose);

    return () => synth.removeEventListener('voiceschanged', choose);
  }, [supported, lang]);

  // Speech outlives the page that started it: leave mid-sentence without this and the browser keeps
  // reading a form nobody is looking at any more.
  useEffect(() => {
    if (!supported) return;

    return () => {
      runRef.current += 1;
      window.speechSynthesis.cancel();
    };
  }, [supported]);

  const stop = useCallback(() => {
    if (!supported) return;

    runRef.current += 1;
    window.speechSynthesis.cancel();
    setState('idle');
  }, [supported]);

  const speak = useCallback(
    (text: string) => {
      if (!supported) return;

      const synth = window.speechSynthesis;
      const run = runRef.current + 1;
      runRef.current = run;

      // Anything already queued belongs to a run this one has just superseded.
      synth.cancel();

      const chunks = intoChunks(text);
      if (chunks.length === 0) return;

      let index = 0;

      function next() {
        // A newer run, or a stop, has taken over since this utterance was queued.
        if (runRef.current !== run) return;

        if (index >= chunks.length) {
          setState('idle');
          return;
        }

        const utterance = new SpeechSynthesisUtterance(chunks[index]);
        index += 1;

        utterance.lang = lang;
        if (voiceRef.current) utterance.voice = voiceRef.current;

        utterance.onend = next;
        utterance.onerror = (event) => {
          // Our own cancel() reports as an error on whatever was mid-sentence. Only a real fault
          // should stop the run and put the control back to its resting state.
          if (event.error === 'interrupted' || event.error === 'canceled') return;
          if (runRef.current !== run) return;

          console.error('Read aloud stopped', event.error);
          setState('idle');
        };

        synth.speak(utterance);
      }

      setState('speaking');
      next();
    },
    [supported, lang],
  );

  const pause = useCallback(() => {
    if (!supported) return;

    window.speechSynthesis.pause();
    setState('paused');
  }, [supported]);

  const resume = useCallback(() => {
    if (!supported) return;

    window.speechSynthesis.resume();
    setState('speaking');
  }, [supported]);

  return { supported, state, speak, pause, resume, stop };
}

/**
 * Long enough to keep sentences whole, short enough to stay well inside Chrome's cut-off.
 *
 * At a default speaking rate this is around ten seconds, against a limit of roughly fifteen.
 */
const maxChunkLength = 180;

/**
 * Splits text into utterance-sized pieces, on sentence ends where it can and on word boundaries
 * where a sentence is too long to fit.
 *
 * Never mid-word: a chunk boundary is a short pause in the reading, and one dropped into the middle
 * of a word is heard as two wrong words.
 */
function intoChunks(text: string): string[] {
  const chunks: string[] = [];

  for (const raw of text.match(/[^.!?\n]+[.!?]*\n*/g) ?? []) {
    const sentence = raw.trim();
    if (sentence.length === 0) continue;

    if (sentence.length <= maxChunkLength) {
      chunks.push(sentence);
      continue;
    }

    let current = '';

    for (const word of sentence.split(/\s+/)) {
      if (current.length > 0 && current.length + word.length + 1 > maxChunkLength) {
        chunks.push(current);
        current = word;
      } else {
        current = current.length === 0 ? word : `${current} ${word}`;
      }
    }

    if (current.length > 0) chunks.push(current);
  }

  return chunks;
}
